
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../tailwindcss/Tailwind.css';

// Import components
import Header from './components/Header';
import ChatArea from './components/ChatArea';
import InputArea from './components/InputArea';
import FileUploadModal from './components/FileUploadModal';

// Import API service
import metadataExtractionService from './api';

const MetadataExtraction = () => {
  const navigate = useNavigate();
  
  // State for models
  const [models, setModels] = useState([]);
  const [primaryModel, setPrimaryModel] = useState('');
  const [secondaryModel, setSecondaryModel] = useState('');
  
  // State for chat
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'system',
      content: 'Welcome to the Neo4j to SQL Metadata Extraction System. Upload a CSV file with metadata or a procedure file (PDF/TXT) to generate SQL queries.'
    }
  ]);
  const [input, setInput] = useState('');
  
  // State for file upload
  const [fileUploading, setFileUploading] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [uploadType, setUploadType] = useState('metadata');
  const [databaseName, setDatabaseName] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Refs
  const fileInputRef = useRef(null);
  const procedureFileInputRef = useRef(null);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      const response = await metadataExtractionService.getModels();
      const data = response.models;
      setModels(data);
      if (data.length > 0) {
        setPrimaryModel(data[0].name);
        setSecondaryModel(data.length > 1 ? data[1].name : data[0].name);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || !fileUploaded) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    const loadingMessageId = Date.now();
    setMessages(prev => [...prev, { role: 'assistant', content: '', isLoading: true, id: loadingMessageId }]);

    try {
      const data = await metadataExtractionService.sendQuery(input, primaryModel, secondaryModel);
      
      let botResponse = { role: 'assistant', content: data.answer, id: loadingMessageId };

      // If there's SQL query, add it to the response
      if (data.sql_query) {
        botResponse.sql_query = data.sql_query;
      }

      setMessages(prev => prev.map(msg =>
        msg.id === loadingMessageId ? botResponse : msg
      ));

      // If SQL query exists, execute it
      if (data.sql_query) {
        await executeSqlQuery(data.sql_query);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => prev.map(msg =>
        msg.id === loadingMessageId ? {
          role: 'assistant',
          content: `Error: ${error.message || 'Unable to connect to the server. Please try again later.'}`,
          id: loadingMessageId
        } : msg
      ));
    } finally {
      setLoading(false);
    }
  };

  const executeSqlQuery = async (sqlQuery) => {
    try {
      const data = await metadataExtractionService.executeSqlQuery(sqlQuery);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'SQL Query Results:',
        sqlResults: data
      }]);
    } catch (error) {
      console.error('Error executing SQL query:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Error executing SQL query. Please check the syntax and try again.'
      }]);
    }
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setSelectedFile(file);
    setUploadType(type);
  };
  
  const handleUploadSelectedFile = async () => {
    if (!selectedFile) return;
    
    if (uploadType === 'procedure' && !databaseName.trim()) {
      alert('Please enter a database name');
      return;
    }
    
    // Add a file uploading message to the chat
    const uploadingMessageId = Date.now();
    setMessages(prev => [...prev, {
      role: 'user',
      content: `Uploading ${uploadType === 'metadata' ? 'CSV metadata' : 'procedure'} file: ${selectedFile.name}...`
    }]);

    setMessages(prev => [...prev, {
      role: 'assistant',
      content: '',
      isLoading: true,
      id: uploadingMessageId,
      isFileUpload: true
    }]);

    setFileUploading(true);

    try {
      let data;
      
      if (uploadType === 'metadata') {
        data = await metadataExtractionService.uploadMetadataFile(selectedFile);
      } else {
        data = await metadataExtractionService.uploadProcedureFile(selectedFile, databaseName);
      }

      if (data.success) {
        let successMessage = '';
        
        if (uploadType === 'metadata') {
          successMessage = `CSV file uploaded successfully! ${data.csv_info?.rows || 'Unknown'} rows and ${data.csv_info?.columns || 'Unknown'} columns processed.`;
        } else {
          successMessage = `Procedure file uploaded successfully! File: ${data.filename}, Size: ${(data.file_size / 1024).toFixed(2)} KB, Database: ${data.database}`;
          
          // Add info about external upload if available
          if (data.external_upload) {
            if (data.external_upload.success) {
              successMessage += "\nExternal processing completed successfully.";
            } else {
              successMessage += "\nNote: External processing unavailable. Local processing only.";
            }
          }
        }
        
        setMessages(prev => prev.map(msg =>
          msg.id === uploadingMessageId ? {
            role: 'assistant',
            content: successMessage,
            id: uploadingMessageId
          } : msg
        ));
        setFileUploaded(true);
      } else {
        setMessages(prev => prev.map(msg =>
          msg.id === uploadingMessageId ? {
            role: 'assistant',
            content: `Error uploading file: ${data.error || 'Unknown error'}`,
            id: uploadingMessageId
          } : msg
        ));
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setMessages(prev => prev.map(msg =>
        msg.id === uploadingMessageId ? {
          role: 'assistant',
          content: `Error: ${error.message || 'Unable to upload file. Please try again later.'}`,
          id: uploadingMessageId
        } : msg
      ));
    } finally {
      setFileUploading(false);
      // Reset file input and selected file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (procedureFileInputRef.current) {
        procedureFileInputRef.current.value = '';
      }
      setSelectedFile(null);
      // Close modal after upload
      setShowUploadModal(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen p-4 sm:p-6">
      <div>
        {/* Header */}
        <Header 
          navigate={navigate}
          models={models}
          primaryModel={primaryModel}
          setPrimaryModel={setPrimaryModel}
          secondaryModel={secondaryModel}
          setSecondaryModel={setSecondaryModel}
        />

        {/* Chat Area */}
        <div className="bg-white rounded-xl shadow-md">
          <ChatArea messages={messages} />

          {/* Input Area */}
          <InputArea 
            input={input}
            setInput={setInput}
            handleSendMessage={handleSendMessage}
            setShowUploadModal={setShowUploadModal}
            loading={loading}
            fileUploading={fileUploading}
            fileUploaded={fileUploaded}
          />
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <FileUploadModal 
          uploadType={uploadType}
          setUploadType={setUploadType}
          databaseName={databaseName}
          setDatabaseName={setDatabaseName}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          fileInputRef={fileInputRef}
          procedureFileInputRef={procedureFileInputRef}
          handleFileSelect={handleFileSelect}
          handleUploadSelectedFile={handleUploadSelectedFile}
          fileUploading={fileUploading}
          setShowUploadModal={setShowUploadModal}
        />
      )}
    </div>
  );
};

export default MetadataExtraction;